import React from 'react';

export function ScoringTechniques() {
  return (
    <div className="space-y-6 text-white/90" style={{ userSelect: "none" }}>
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Scoring Techniques and Patterns</h3>
        <p className="mb-4">
          Scoring is both a functional and artistic aspect of bread baking. The right scoring pattern not only controls how your bread expands during baking but also creates beautiful designs that make each loaf unique.
        </p>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Basic Scoring Principles</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Purpose of Scoring</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Controls where the bread expands during oven spring</li>
              <li>Prevents random bursting of the crust</li>
              <li>Creates an attractive appearance</li>
              <li>Influences the final shape of the loaf</li>
              <li>Affects crust texture and development</li>
            </ul>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Essential Tools</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Lame (curved or straight blade holder)</li>
              <li>Sharp razor blade</li>
              <li>Scissors (for certain patterns)</li>
              <li>Spray bottle with water (optional)</li>
            </ul>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Basic Techniques</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Score at a 30-45 degree angle for "ears"</li>
              <li>Score straight down for more controlled expansion</li>
              <li>Make cuts about ¼ to ½ inch deep</li>
              <li>Score quickly and decisively</li>
              <li>Keep blade clean between cuts</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Common Scoring Patterns</h3>
        
        <div className="space-y-6">
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Single Slash</h4>
            <p className="text-sm text-white/80 mt-1">
              The most basic and versatile scoring pattern.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                <li>Score at 30-45 degree angle</li>
                <li>Make one confident cut across the loaf</li>
                <li>Cut should be about ⅓ the length of the loaf</li>
                <li>Ideal for batards and oval loaves</li>
              </ul>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Cross or Hash Pattern</h4>
            <p className="text-sm text-white/80 mt-1">
              Traditional pattern for round loaves.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                <li>Score two perpendicular lines across center</li>
                <li>Cut straight down or at slight angle</li>
                <li>Make cuts equal length and depth</li>
                <li>Optional: Add diagonal cuts in each quadrant</li>
              </ul>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Wheat Stalk</h4>
            <p className="text-sm text-white/80 mt-1">
              Decorative pattern that resembles a wheat stalk.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                <li>Make one long central cut</li>
                <li>Add angled cuts along both sides</li>
                <li>Keep cuts shallow and consistent</li>
                <li>Works best on oval or batard shapes</li>
              </ul>
            </div>
          </div>
          
          <div className="border-l-4 border-amber-400 pl-4">
            <h4 className="font-medium">Square Pattern</h4>
            <p className="text-sm text-white/80 mt-1">
              Creates a checkerboard effect on round loaves.
            </p>
            <div className="mt-3">
              <h5 className="text-amber-400 text-sm">Technique:</h5>
              <ul className="list-disc list-inside text-sm text-white/80 mt-1">
                <li>Score parallel lines in one direction</li>
                <li>Rotate 90 degrees and repeat</li>
                <li>Keep cuts straight and evenly spaced</li>
                <li>Score at consistent depth</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Advanced Scoring Techniques</h3>
        
        <div className="space-y-4">
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Double Scoring</h4>
            <p className="text-sm text-white/80">
              Creating depth and dimension with overlapping cuts.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Make primary cuts deeper (½ inch)</li>
              <li>Add secondary cuts shallower (¼ inch)</li>
              <li>Vary angles between cuts</li>
              <li>Consider how cuts will interact during oven spring</li>
            </ul>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Stenciling</h4>
            <p className="text-sm text-white/80">
              Using stencils with flour for decorative effects.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Place stencil on proofed dough</li>
              <li>Dust with rice flour or cocoa powder</li>
              <li>Carefully remove stencil</li>
              <li>Score around design if needed</li>
            </ul>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <h4 className="text-lg text-amber-400 mb-2">Scissor Cuts</h4>
            <p className="text-sm text-white/80">
              Creating unique textures with scissors.
            </p>
            <ul className="list-disc list-inside text-sm text-white/80 mt-2">
              <li>Hold scissors at 45-degree angle</li>
              <li>Make quick, decisive snips</li>
              <li>Create patterns by varying cut angles</li>
              <li>Works well for decorative "ears" or spikes</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-white mb-4">Scoring Different Dough Types</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-white/80">
            <thead>
              <tr className="border-b border-white/20">
                <th className="text-left py-3 px-4 text-amber-400">Dough Type</th>
                <th className="text-left py-3 px-4 text-amber-400">Scoring Characteristics</th>
                <th className="text-left py-3 px-4 text-amber-400">Recommended Patterns</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">High Hydration</td>
                <td className="py-3 px-4">Requires shallow, quick cuts</td>
                <td className="py-3 px-4">Simple patterns, single slash</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Low Hydration</td>
                <td className="py-3 px-4">Holds patterns well</td>
                <td className="py-3 px-4">Complex decorative patterns</td>
              </tr>
              <tr className="border-b border-white/10">
                <td className="py-3 px-4 font-medium">Whole Grain</td>
                <td className="py-3 px-4">Can be more resistant</td>
                <td className="py-3 px-4">Deeper cuts, simpler patterns</td>
              </tr>
              <tr>
                <td className="py-3 px-4 font-medium">Enriched Dough</td>
                <td className="py-3 px-4">Soft, may stick to blade</td>
                <td className="py-3 px-4">Snips, cross pattern</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="bg-gray-600/60 p-6">
        <h3 className="text-xl text-amber-400 mb-4">Troubleshooting Scoring Issues</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="text-lg text-white mb-2">Dough Sticking to Blade</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Wet the blade before each cut</li>
              <li>Score more quickly and decisively</li>
              <li>Chill dough before scoring</li>
              <li>Use a sharper blade</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Uneven Expansion</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Ensure cuts are even depth</li>
              <li>Make cuts symmetrical</li>
              <li>Consider dough placement in oven</li>
              <li>Check for consistent proofing</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Poor "Ear" Development</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Adjust blade angle (30-45 degrees)</li>
              <li>Score slightly off-center</li>
              <li>Ensure adequate steam during baking</li>
              <li>Check dough surface tension</li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-lg text-white mb-2">Pattern Disappearing During Baking</h4>
            <ul className="list-disc list-inside text-sm text-white/80 space-y-1">
              <li>Make cuts deeper</li>
              <li>Score cold dough</li>
              <li>Simplify pattern</li>
              <li>Check for overproofing</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}